import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SwapHoriz as ExchangeIcon,
  Assessment as StatsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import dataExchangeAssetsService, { DataExchangeAsset } from '../services/dataExchangeAssetsService';

const DataExchangeCatalog: React.FC = () => {
  const [assets, setAssets] = useState<DataExchangeAsset[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<DataExchangeAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<DataExchangeAsset | null>(null);
  const [stats, setStats] = useState<any>(null);

  const [formData, setFormData] = useState<Partial<DataExchangeAsset>>({
    systemName: '',
    sourceSystems: [],
    targetSystems: [],
    exchangeType: '',
    piaReference: '',
    usageSummary: '',
    status: 'Active',
    frequency: 'Daily'
  });

  useEffect(() => {
    fetchAssets();
    fetchStats();
  }, []);

  useEffect(() => {
    filterAssets();
  }, [assets, searchTerm, statusFilter]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const data = await dataExchangeAssetsService.getAll();
      console.log('Fetched assets:', data);
      console.log('Number of assets:', data?.length);
      setAssets(data);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await dataExchangeAssetsService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filterAssets = () => {
    let filtered = assets;

    if (searchTerm) {
      filtered = filtered.filter(asset =>
        asset.systemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.exchangeType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.usageSummary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.sourceSystems?.some(s => s?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        asset.targetSystems?.some(t => t?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter !== 'All') {
      filtered = filtered.filter(asset => asset.status === statusFilter);
    }

    setFilteredAssets(filtered);
  };

  const handleOpenDialog = (asset?: DataExchangeAsset) => {
    if (asset) {
      setEditingAsset(asset);
      setFormData(asset);
    } else {
      setEditingAsset(null);
      setFormData({
        systemName: '',
        sourceSystems: [],
        targetSystems: [],
        exchangeType: '',
        piaReference: '',
        usageSummary: '',
        status: 'Active',
        frequency: 'Daily'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAsset(null);
  };

  const handleSave = async () => {
    try {
      if (editingAsset) {
        await dataExchangeAssetsService.update(editingAsset._id!, formData);
      } else {
        await dataExchangeAssetsService.create(formData);
      }
      fetchAssets();
      fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving asset:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this data exchange asset?')) {
      try {
        await dataExchangeAssetsService.delete(id);
        fetchAssets();
        fetchStats();
      } catch (error) {
        console.error('Error deleting asset:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'default';
      case 'Deprecated': return 'error';
      case 'In Development': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <ExchangeIcon sx={{ mr: 2, fontSize: 40 }} />
            Data Exchange Asset Catalog
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Unidatum-Style Source-Target Exchange Management
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAssets}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: 'primary.main' }}
          >
            Add Exchange
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ border: '2px solid #1976D2' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                      {stats?.totalAssets || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Exchanges
                    </Typography>
                  </Box>
                  <StatsIcon sx={{ fontSize: 40, color: '#1976D2', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ border: '2px solid #2E7D32' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                      {stats?.activeAssets || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Exchanges
                    </Typography>
                  </Box>
                  <ExchangeIcon sx={{ fontSize: 40, color: '#2E7D32', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ border: '2px solid #F57C00' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                      {stats?.exchangeTypes?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Exchange Types
                    </Typography>
                  </Box>
                  <StatsIcon sx={{ fontSize: 40, color: '#F57C00', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ border: '2px solid #7B1FA2' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                      {stats?.totalAssets && stats?.activeAssets ? ((stats.activeAssets / stats.totalAssets) * 100).toFixed(0) : 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Rate
                    </Typography>
                  </Box>
                  <StatsIcon sx={{ fontSize: 40, color: '#7B1FA2', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search by system name, exchange type, source, target, or usage..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="All">All Statuses</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
                <MenuItem value="In Development">In Development</MenuItem>
                <MenuItem value="Deprecated">Deprecated</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>System Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Source System(s)</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Target System(s)</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Exchange Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PIA/PTA Reference</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Usage Summary</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Loading...</TableCell>
              </TableRow>
            ) : !filteredAssets || filteredAssets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">No data exchange assets found</TableCell>
              </TableRow>
            ) : (
              filteredAssets.map((asset) => (
                <TableRow key={asset._id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {asset.systemName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {asset.sourceSystems?.join(', ') || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {asset.targetSystems?.join(', ') || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={asset.exchangeType} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {asset.piaReference || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={asset.usageSummary || ''}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }} noWrap>
                        {asset.usageSummary || 'N/A'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={asset.status} size="small" color={getStatusColor(asset.status) as any} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenDialog(asset)} color="primary">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(asset._id!)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Add Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAsset ? 'Edit Data Exchange Asset' : 'Add New Data Exchange Asset'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="System Name"
                value={formData.systemName}
                onChange={(e) => setFormData({ ...formData, systemName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Source Systems (comma-separated)"
                value={formData.sourceSystems?.join(', ')}
                onChange={(e) => setFormData({ ...formData, sourceSystems: e.target.value.split(',').map(s => s.trim()) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Target Systems (comma-separated)"
                value={formData.targetSystems?.join(', ')}
                onChange={(e) => setFormData({ ...formData, targetSystems: e.target.value.split(',').map(s => s.trim()) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Exchange Type"
                value={formData.exchangeType}
                onChange={(e) => setFormData({ ...formData, exchangeType: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PIA/PTA Reference"
                value={formData.piaReference}
                onChange={(e) => setFormData({ ...formData, piaReference: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Usage Summary"
                value={formData.usageSummary}
                onChange={(e) => setFormData({ ...formData, usageSummary: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="In Development">In Development</MenuItem>
                  <MenuItem value="Deprecated">Deprecated</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formData.frequency}
                  label="Frequency"
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
                >
                  <MenuItem value="Real-time">Real-time</MenuItem>
                  <MenuItem value="Hourly">Hourly</MenuItem>
                  <MenuItem value="Daily">Daily</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="On-Demand">On-Demand</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: 'primary.main' }}>
            {editingAsset ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DataExchangeCatalog;
